import React, { useState } from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';
import { ListItemComponentAnalytics } from 'shared/analytics/types/componentEvents';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { isWeb } from 'shared/constants/platform';
import { useComponentAnalytics } from 'shared/hooks/analytics/useComponentAnalytics';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

import ListItemLeadingAccessory, {
  ListItemLeadingAccessoryProps,
} from './ListItemLeadingAccessory';
import ListItemTextStack, { ListItemLineFormat } from './ListItemTextStack';
import ListItemTrailingAccessory, {
  ListItemTrailingAccessoryProps,
} from './ListItemTrailingAccessory';

type ListItemColorTheme = 'white' | 'surface' | 'transparent';

export interface ListItemProps extends PressableAnalyticsProps {
  testID?: string;
  /** Overrides the primary label ID independently of the row ID. */
  primaryCopyTestID?: string;
  /**
   * the primary text of the label.
   */
  primaryCopy: string;
  primaryNumberOfLines?: number;
  /**
   * optional caption or label text
   */
  secondaryCopy?: Maybe<string>;
  secondaryNumberOfLines?: number;
  /**
   * `caption` displays `secondaryCopy` under the list item. `label` displays above it.
   */
  lineFormat?: Maybe<ListItemLineFormat>;
  trailingAccessory?: Maybe<ListItemTrailingAccessoryProps>;
  leadingAccessory?: Maybe<ListItemLeadingAccessoryProps>;
  /**
   * blocks actions and mutes the presentation of the ListItem.
   */
  isDisabled?: Maybe<boolean>;
  isSelected?: boolean;
  accessibilityLabel?: string;
  /**
   * optionally displays the chevron.
   */
  showsChevron?: Maybe<boolean>;
  /**
   * optionally displays the edit.
   */
  showsEditButton?: Maybe<boolean>;

  /**
   * the react native text layout behavior is somewhat unpredictable. there are some
   * cases, especially around text wrapping, where `flexBasis: 'auto'` leads to unexpected
   * outcomes. in these cases, you can use `trailingAccessoryContainerStyle` to inject custom
   * style to try to correct known edge cases.
   *
   * NOTE: it's nearly impossible to anticipate the behavior of text across all devices!
   * only use this as a last resort.
   */
  trailingAccessoryContainerStyle?: Maybe<ViewStyle>;

  onPress?: Maybe<() => void>;
  /**
   * optionally make the primary text bold
   */
  boldPrimaryCopy?: Maybe<boolean>;

  /**
   * optionally provide a background color
   */
  colorTheme?: ListItemColorTheme;

  /** Fires a `List Item Pressed` component analytics event on press. */
  componentAnalytics?: ListItemComponentAnalytics;
}

// we only want our trailing accessory to flex if it has substantial dynamic text
// we need to allow to shrink and truncate. use this function to opt into flex
// behavior if you're adding a new variable text accessory to ListItemTrailingAccessory.
const styleForChild = (
  trailingAccessory: Maybe<ListItemTrailingAccessoryProps>,
) => {
  if (
    trailingAccessory?.name === VXLComponentId.LIST_ITEM_TEXT_STACK ||
    trailingAccessory?.name === VXLComponentId.TAG
  ) {
    return styles.listItemTrailingAccessoryContainerFlex;
  }
  return null;
};

const ListItemContent = ({
  primaryCopy,
  secondaryCopy,
  lineFormat = ListItemLineFormat.CAPTION,
  leadingAccessory,
  trailingAccessory,
  isDisabled = false,
  showsChevron = true,
  showsEditButton = false,
  primaryNumberOfLines,
  secondaryNumberOfLines,
  trailingAccessoryContainerStyle,
  boldPrimaryCopy = false,
  primaryCopyTestID,
}: ListItemProps) => {
  const textProps = {
    primaryCopy,
    primaryCopyTestID,
    primaryNumberOfLines,
    secondaryCopy,
    secondaryNumberOfLines,
    isDisabled,
    lineFormat,
    boldPrimaryCopy,
  };
  const chevronColor = isDisabled
    ? theme.color.interactive.disabled.text
    : theme.color.text.primary;

  return (
    <View style={styles.contentContainer}>
      {leadingAccessory ? (
        <ListItemLeadingAccessory
          accessory={leadingAccessory}
          isDisabled={Boolean(isDisabled)}
        />
      ) : null}
      <View
        style={[
          styles.listItemTextContainer,
          trailingAccessory?.name === VXLComponentId.TAG
            ? styles.maxWidth
            : null,
        ]}
      >
        <ListItemTextStack {...textProps} />
      </View>
      {trailingAccessory ? (
        <View
          style={[
            styleForChild(trailingAccessory),
            trailingAccessoryContainerStyle,
          ]}
        >
          <ListItemTrailingAccessory
            accessory={trailingAccessory}
            isDisabled={Boolean(isDisabled)}
          />
        </View>
      ) : null}

      {showsEditButton && !showsChevron ? (
        <View style={styles.chevronContainer} testID="list-item-edit">
          <Icon iconKey="EDIT" iconColor={chevronColor} />
        </View>
      ) : null}

      {showsChevron && !showsEditButton ? (
        <View style={styles.chevronContainer} testID="list-item-chevron">
          <Icon iconKey="CHEVRON_RIGHT" iconColor={chevronColor} />
        </View>
      ) : null}
    </View>
  );
};

const ListItem = (props: ListItemProps) => {
  const {
    onPress,
    isDisabled = false,
    isSelected,
    accessibilityLabel,
    analyticsId = '',
    customAttributes = null,
    colorTheme = 'white',
    componentAnalytics,
    testID,
  } = props;
  const themeStyles = colorThemeStyles[colorTheme];
  const { logListItemPressed } = useComponentAnalytics();

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handlePress = () => {
    if (isWeb) {
      // Clear focus/press state on web before navigation runs.
      // `onPressOut` may not fire if the screen is detached/hidden by
      // a router push, leaving the focus outline lingering when the
      // user navigates back.
      setIsFocused(false);
      setIsPressed(false);
    }

    if (componentAnalytics) {
      logListItemPressed({
        item_name: componentAnalytics.itemName,
        item_position: componentAnalytics.itemPosition,
        category: componentAnalytics.category,
        sub_category: componentAnalytics.subCategory,
      });
    }

    onPress?.();
  };

  if (onPress) {
    return (
      <PressableWithDelay
        analyticsId={analyticsId}
        customAttributes={customAttributes}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => {
          setIsPressed(false);
        }}
        onPress={handlePress}
        disabled={Boolean(isDisabled)}
        aria-disabled={Boolean(isDisabled)}
        accessibilityState={
          isSelected === undefined
            ? undefined
            : { selected: isSelected, disabled: Boolean(isDisabled) }
        }
        accessible
        accessibilityLabel={accessibilityLabel}
        role="button"
        style={[
          styles.button,
          themeStyles.container,
          isFocused && themeStyles.focused,
          isHovered && themeStyles.hovered,
          isPressed && themeStyles.pressed,
        ]}
        testID={testID ?? 'PressableListItem'}
      >
        <ListItemContent {...props} />
      </PressableWithDelay>
    );
  }

  return (
    <View
      style={themeStyles.container}
      aria-disabled={Boolean(isDisabled)}
      accessibilityLabel={accessibilityLabel}
      testID={testID ?? 'StaticListItem'}
    >
      <ListItemContent {...props} />
    </View>
  );
};

const styles = StyleSheet.create({
  // note: border added in all cases to prevent subtile 2pt resize
  // on focus mode.
  button: {
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineWidth: 0,
      },
    }),
  },
  chevronContainer: {
    height: 24,
    width: 24,
  },
  contentContainer: {
    alignItems: 'center',
    columnGap: theme.spacing.m,
    flexDirection: 'row',
    margin: theme.spacing.m,
  },
  listItemTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
    minWidth: 100,
    overflow: 'hidden',
  },
  listItemTrailingAccessoryContainerFlex: {
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  maxWidth: {
    maxWidth: '65%',
  },
});

interface ColorThemeStyle {
  container: ViewStyle;
  hovered?: ViewStyle;
  pressed?: ViewStyle;
  focused?: ViewStyle;
}

const colorThemeStyles: Record<ListItemColorTheme, ColorThemeStyle> = {
  white: StyleSheet.create({
    container: {
      backgroundColor: theme.color.interactive.secondary,
      borderColor: theme.color.interactive.secondary,
      borderWidth: 1,
    },
    focused: {
      borderColor: theme.color.interactive.focused.border,
    },
    hovered: {
      backgroundColor: theme.color.interactive.secondaryHovered,
      borderColor: theme.color.interactive.secondaryHovered,
    },
    pressed: {
      backgroundColor: theme.color.interactive.secondaryPressed,
      borderColor: theme.color.interactive.secondaryPressed,
    },
  }),
  surface: StyleSheet.create({
    /* eslint-disable react-native/no-unused-styles */
    container: {
      backgroundColor: theme.color.interactive.surface,
      borderColor: theme.color.interactive.surface,
      borderWidth: 1,
    },
    focused: {
      borderColor: theme.color.interactive.focused.border,
    },
    hovered: {
      backgroundColor: theme.color.interactive.surfaceHovered,
      borderColor: theme.color.interactive.surfaceHovered,
    },
    pressed: {
      backgroundColor: theme.color.interactive.surfacePressed,
      borderColor: theme.color.interactive.surfacePressed,
    },
  }),
  transparent: StyleSheet.create({
    container: {
      backgroundColor: 'transparent',
    },
  }),
};

export default ListItem;
